/**
 * Mock fixture 注册中心 · W2-9 PR-1 mock 基础设施
 *
 * 承接：CLAUDE.md §11.5.1 数据工厂
 *
 * 用法：
 *   ```ts
 *   import { registerFixture } from '@/lib/fixtures/registry';
 *
 *   registerFixture({
 *     pathname: '/admin/v1/sessions/recent',
 *     methods: ['GET'],
 *     resolve: () => ({ data: { items: [...], total: 0 } }),
 *   });
 *   ```
 *
 * PR-1：注册中心 + path 匹配（精确 + :param 模板）+ axios 拦截器对接接口
 * PR-2：填具体 fixture（M01 sessions / traces / 数据工厂场景脚本）
 */

import type { AxiosRequestConfig } from "axios";

import type { MockFixture, MockRequestContext, MockResponse } from "./types";

const fixtures: MockFixture[] = [];

/**
 * 注册一条 fixture
 */
export function registerFixture<T>(fixture: MockFixture<T>): void {
	fixtures.push(fixture as MockFixture);
}

/**
 * 清空注册中心（仅测试环境用）
 */
export function clearFixtures(): void {
	fixtures.length = 0;
}

/**
 * 列出已注册 fixture（供 /dev-console 调试展示用）
 */
export function listFixtures(): ReadonlyArray<MockFixture> {
	return fixtures;
}

/**
 * 解析 axios 请求 → 命中 fixture → MockResponse
 *
 * 返回 null 表示无命中（拦截器应放行或返 404）。
 */
export async function resolveFixture(config: AxiosRequestConfig): Promise<MockResponse | null> {
	const method = (config.method ?? "get").toUpperCase();
	const url = config.url ?? "";
	const fullUrl = config.baseURL ? joinUrl(config.baseURL, url) : url;

	const parsed = parseUrl(fullUrl);

	for (const fixture of fixtures) {
		const allowedMethods = (fixture.methods ?? ["GET"]).map((m) => m.toUpperCase());
		if (!allowedMethods.includes(method)) continue;

		const params = matchPathname(fixture.pathname, parsed.pathname);
		if (params === null) continue;

		const ctx: MockRequestContext = {
			method,
			url: fullUrl,
			pathname: parsed.pathname,
			params,
			query: { ...parsed.query, ...normalizeAxiosParams(config.params) },
			body: config.data,
		};

		return await fixture.resolve(ctx);
	}

	return null;
}

// ─── helpers ──────────────────────────────────────────────────────────────

function joinUrl(base: string, path: string): string {
	if (/^https?:\/\//.test(path)) return path;
	const trimmedBase = base.replace(/\/+$/, "");
	const trimmedPath = path.replace(/^\/+/, "");
	return `${trimmedBase}/${trimmedPath}`;
}

interface ParsedUrl {
	pathname: string;
	query: Record<string, string>;
}

function parseUrl(url: string): ParsedUrl {
	try {
		const u = new URL(url, "http://placeholder.local");
		const query: Record<string, string> = {};
		u.searchParams.forEach((v, k) => {
			query[k] = v;
		});
		return { pathname: u.pathname, query };
	} catch {
		const [pathname = url, search = ""] = url.split("?");
		const query: Record<string, string> = {};
		if (search) {
			for (const pair of search.split("&")) {
				const [k = "", v = ""] = pair.split("=");
				if (k) query[decodeURIComponent(k)] = decodeURIComponent(v);
			}
		}
		return { pathname, query };
	}
}

/**
 * 匹配 fixture pathname（支持 `:param` 占位）
 *
 * - `/admin/v1/sessions/recent` 精确匹配
 * - `/admin/v1/trace/:trace_id` 抽出 params.trace_id
 *
 * 不匹配返 null；匹配返 params object（精确匹配返 {}）。
 */
function matchPathname(pattern: string, actual: string): Record<string, string> | null {
	const patternParts = pattern.split("/").filter(Boolean);
	const actualParts = actual.split("/").filter(Boolean);
	if (patternParts.length !== actualParts.length) return null;

	const params: Record<string, string> = {};
	for (let i = 0; i < patternParts.length; i++) {
		const p = patternParts[i] as string;
		const a = actualParts[i] as string;
		if (p.startsWith(":")) {
			params[p.slice(1)] = decodeURIComponent(a);
		} else if (p !== a) {
			return null;
		}
	}
	return params;
}

function normalizeAxiosParams(params: unknown): Record<string, string> {
	if (params === null || params === undefined) return {};
	if (typeof params !== "object") return {};
	const out: Record<string, string> = {};
	for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
		if (v !== undefined && v !== null) out[k] = String(v);
	}
	return out;
}
